/* PDF export for the "Financial Metric Detail" popups (Grand Total, Gross
 * Total, Total Payments, Total Expenses, Current Deposits, Remaining
 * Collectibles).
 *
 * Layout (A4 portrait, school theme — maroon + gold like the app):
 *   - Letterhead band: school logo, school name + second line, report kind.
 *   - Title, period/generated line, and highlight boxes (headline total,
 *     counts).
 *   - The breakdown table (grade rows bold, student rows muted) with a
 *     totals footer.
 *   - Prepared-by / Noted-by signature lines.
 *   - Every page: slim running header after page 1, gold rule + footer with
 *     "Page X of Y".
 *
 * Letterhead comes from currentSchool() — the same source as payslips,
 * receipts and the SOA — with the bundled NCLA logo as a fallback.
 * jsPDF's Helvetica is Latin-1 only, so text goes through pdfSafe() and
 * amounts print as "PHP 1,234.00" instead of the ₱ glyph. */

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { currentSchool } from "./currentSchool";

const MAROON = [107, 29, 29];
const MAROON_DARK = [74, 18, 18];
const GOLD   = [201, 162, 39];
const GOLD_SOFT = [250, 242, 221];
const CREAM  = [253, 251, 244];
const INK    = [26, 26, 26];
const MUTED  = [120, 110, 95];

const FALLBACK_LOGO = "/ncla-logo.png";

/* ── helpers ─────────────────────────────────────────────────────────── */

function pdfSafe(s) {
  if (s == null) return "";
  return String(s)
    .replace(/₱/g, "PHP ")
    .replace(/[—–−]/g, "-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/·/g, "·")
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\xFF]/g, "");
}
const money = (v) =>
  Number(v || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const php = (v) => `PHP ${money(v)}`;
const neg = (v) => (v > 0.005 ? `-${money(v)}` : "-");

function slug(s) {
  return String(s || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function currentUserName() {
  try {
    const u = JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
    return u?.linkedEntity?.displayName || u?.fullName || u?.name || u?.username || "";
  } catch { return ""; }
}

/** Load an image URL as a PNG data URL (null if it can't be loaded — e.g.
 *  a remote logo without CORS headers). */
function loadImageData(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve({ data: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/* ── per-metric table ─────────────────────────────────────────────────── */

const stuLabel = (stu) => `      ${stu.name}${stu.studentNumber ? `  ·  ${stu.studentNumber}` : ""}`;

/**
 * Table + highlight boxes for one detail type.
 * Returns { head, body, foot, sub[], right:Set, highlights:[{label,value}] }.
 */
function tableFor(detail, rows, kpis) {
  const body = [];
  const sub = [];
  const push = (cells, isSub = false) => { body.push(cells.map(pdfSafe)); sub.push(isSub); };
  const sumCount = rows.reduce((n, r) => n + (r.count || 0), 0);
  const levels = { label: "Grade levels", value: String(rows.length) };
  const studentsBox = { label: "Students", value: sumCount.toLocaleString("en-PH") };

  switch (detail.type) {
    case "grand":
      for (const r of rows) {
        push([r.label, money(r.feeAmt), String(r.count), money(r.subtotal)]);
        for (const stu of r.students || []) push([stuLabel(stu), "", "", money(stu.amount)], true);
      }
      return {
        head: [["Grade Level", "Fee Structure", "Students", "Subtotal"]],
        body, sub, right: new Set([1, 2, 3]),
        foot: [["TOTAL", "", String(sumCount), money(kpis?.grandTotal ?? 0)]],
        highlights: [{ label: "Grand total", value: php(kpis?.grandTotal ?? 0), main: true }, levels, studentsBox],
      };

    case "gross": {
      for (const r of rows) {
        push([r.label, String(r.count), money(r.gross), neg(r.discount)]);
        for (const stu of r.students || []) push([stuLabel(stu), "", money(stu.gross), neg(stu.discount)], true);
      }
      const disc = kpis && kpis.grandTotal > kpis.grossTotal ? kpis.grandTotal - kpis.grossTotal : 0;
      return {
        head: [["Grade Level", "Students", "Gross Total", "Discounts"]],
        body, sub, right: new Set([1, 2, 3]),
        foot: [["TOTAL", String(sumCount), money(kpis?.grossTotal ?? 0), neg(disc)]],
        highlights: [
          { label: "Gross total", value: php(kpis?.grossTotal ?? 0), main: true },
          { label: "Discounts", value: disc > 0.005 ? php(disc) : "-" },
          studentsBox,
        ],
      };
    }

    case "pay": {
      for (const r of rows) push([r.date, r.student, r.method, money(r.amount)]);
      const total = detail.footerTotal ?? kpis?.totalPayments ?? 0;
      return {
        head: [["Date", "Student", "Method", "Amount"]],
        body, sub, right: new Set([3]),
        foot: [[detail.footerLabel || "TOTAL PAYMENTS", "", "", money(total)]],
        highlights: [
          { label: "Total collected", value: php(total), main: true },
          { label: "Payments", value: rows.length.toLocaleString("en-PH") },
          { label: "Average payment", value: rows.length ? php(total / rows.length) : "-" },
        ],
      };
    }

    case "exp": {
      for (const r of rows) push([r.date, r.category, r.description, r.payee, money(r.amount)]);
      const cats = new Set(rows.map(r => r.category)).size;
      return {
        head: [["Date", "Category", "Description", "Paid To", "Amount"]],
        body, sub, right: new Set([4]),
        foot: [["TOTAL EXPENSES", "", "", "", money(kpis?.totalExpenses ?? 0)]],
        highlights: [
          { label: "Total expenses", value: php(kpis?.totalExpenses ?? 0), main: true },
          { label: "Entries", value: String(rows.length) },
          { label: "Categories", value: String(cats) },
        ],
      };
    }

    case "dep": {
      const deficit = detail.currentDeposits < -0.005;
      push(["Total Payments Collected", money(detail.totalPayments)]);
      push(["Less: Total Monthly Expenses", `-${money(detail.totalExpenses)}`]);
      return {
        head: [["Item", "Amount"]],
        body, sub, right: new Set([1]),
        foot: [[deficit ? "NET DEFICIT" : "NET SURPLUS", money(detail.currentDeposits)]],
        highlights: [
          { label: deficit ? "Net deficit" : "Net surplus", value: php(detail.currentDeposits), main: true },
          { label: "Collected", value: php(detail.totalPayments) },
          { label: "Expenses", value: php(detail.totalExpenses) },
        ],
      };
    }

    case "col": {
      const billed = rows.reduce((n, r) => n + (r.gross || 0), 0);
      const paid   = rows.reduce((n, r) => n + (r.paid || 0), 0);
      for (const r of rows) {
        push([r.label, String(r.count), money(r.gross), money(r.paid), money(r.remaining)]);
        for (const stu of r.students || []) {
          push([stuLabel(stu), "", money(stu.gross), money(stu.paid), money(stu.remaining)], true);
        }
      }
      return {
        head: [["Grade Level", "Students", "Billed", "Paid", "Remaining"]],
        body, sub, right: new Set([1, 2, 3, 4]),
        foot: [["TOTAL", String(sumCount), money(billed), money(paid), money(kpis?.remainingCollectibles ?? 0)]],
        highlights: [
          { label: "Remaining to collect", value: php(kpis?.remainingCollectibles ?? 0), main: true },
          { label: "Collected so far", value: billed > 0 ? `${Math.round((paid / billed) * 100)}%` : "-" },
          studentsBox,
        ],
      };
    }

    default:
      return null;
  }
}

/* ── drawing ──────────────────────────────────────────────────────────── */

function drawLetterhead(doc, pageW, school, logo) {
  const bandH = 30;
  doc.setFillColor(...MAROON);
  doc.rect(0, 0, pageW, bandH, "F");
  doc.setFillColor(...MAROON_DARK);
  doc.rect(0, bandH - 4, pageW, 4, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, bandH, pageW, 1.2, "F");

  // Logo on a white disc.
  const cx = 22, cy = 14, r = 10;
  doc.setFillColor(255, 255, 255);
  doc.circle(cx, cy, r, "F");
  if (logo) {
    const size = 17;
    const ratio = logo.w / logo.h || 1;
    const w = ratio >= 1 ? size : size * ratio;
    const h = ratio >= 1 ? size / ratio : size;
    try { doc.addImage(logo.data, "PNG", cx - w / 2, cy - h / 2, w, h); } catch { /* skip logo */ }
  }

  // School name: shrink to fit one line (13pt → 10pt); a very long name
  // wraps to two lines and the second line moves down.
  const nameMaxW = pageW - 37 - 46;
  const name = pdfSafe(school.name).toUpperCase();
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  let size = 13;
  doc.setFontSize(size);
  while (size > 10 && doc.getTextWidth(name) > nameMaxW) { size -= 0.5; doc.setFontSize(size); }
  const nameLines = doc.splitTextToSize(name, nameMaxW).slice(0, 2);
  const nameTop = nameLines.length > 1 ? 9.5 : 12.5;
  doc.text(nameLines, 37, nameTop, { lineHeightFactor: 1.1 });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(245, 225, 190);
  const second = [school.shortName !== school.name ? school.shortName : "", school.address]
    .filter(Boolean).join("  ·  ");
  const secondY = nameTop + (nameLines.length - 1) * size * 0.3528 * 1.1 + 6;
  if (second) doc.text(doc.splitTextToSize(pdfSafe(second), nameMaxW)[0], 37, secondY);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...GOLD);
  doc.text("FINANCIAL REPORT", pageW - 12, 11, { align: "right", charSpace: 0.6 });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(245, 225, 190);
  doc.text(new Date().toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" }),
    pageW - 12, 17, { align: "right" });
  doc.setTextColor(...INK);
  return bandH + 1.2;
}

/** Slim running header for pages after the first. */
function drawRunningHeader(doc, pageW, school, title) {
  doc.setFillColor(...MAROON);
  doc.rect(0, 0, pageW, 10, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 10, pageW, 0.8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text(pdfSafe(school.name).toUpperCase(), 12, 6.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(245, 225, 190);
  doc.text(pdfSafe(title), pageW - 12, 6.5, { align: "right" });
  doc.setTextColor(...INK);
}

function drawHighlights(doc, pageW, y, highlights) {
  const gap = 4, left = 12, width = pageW - 24;
  const boxW = (width - gap * (highlights.length - 1)) / highlights.length;
  const boxH = 17;
  highlights.forEach((h, i) => {
    const x = left + i * (boxW + gap);
    if (h.main) {
      doc.setFillColor(...MAROON);
      doc.roundedRect(x, y, boxW, boxH, 2.5, 2.5, "F");
    } else {
      doc.setFillColor(...CREAM);
      doc.setDrawColor(236, 229, 215);
      doc.roundedRect(x, y, boxW, boxH, 2.5, 2.5, "FD");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.8);
    doc.setTextColor(...(h.main ? GOLD : [138, 109, 40]));
    doc.text(pdfSafe(h.label).toUpperCase(), x + 4, y + 6, { charSpace: 0.3 });
    doc.setFontSize(h.main ? 12 : 11);
    doc.setTextColor(...(h.main ? [255, 255, 255] : INK));
    doc.text(pdfSafe(h.value), x + 4, y + 13);
  });
  doc.setTextColor(...INK);
  return y + boxH;
}

/* ── export ───────────────────────────────────────────────────────────── */

/**
 * Download the open metric popup as a themed PDF.
 * @param {object}  args
 * @param {string}  args.title       Popup title ("Remaining Collectibles").
 * @param {object}  args.detail      The popup's detail object ({ type, … }).
 * @param {Array}   args.rows        Rows in the popup's current sort order —
 *                                   for payments, ALL payments.
 * @param {object}  args.kpis        Same kpis the popup uses for its totals.
 * @param {string} [args.scopeLabel] e.g. "Last 30 days".
 */
export async function exportFinanceDetailPdf({ title, detail, rows, kpis, scopeLabel }) {
  if (!detail) return;
  const table = tableFor(detail, rows || [], kpis);
  if (!table) return;

  const school = currentSchool();
  const logo = (await loadImageData(school.logoUrl)) || (await loadImageData(FALLBACK_LOGO));

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Page 1: letterhead, title, meta line, highlight boxes.
  let y = drawLetterhead(doc, pageW, school, logo) + 10;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...GOLD);
  doc.text("FINANCIAL METRIC DETAIL", 12, y, { charSpace: 0.6 });
  y += 7;
  doc.setFontSize(18);
  doc.setTextColor(...MAROON);
  doc.text(pdfSafe(title), 12, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  const generated = new Date().toLocaleString("en-PH", {
    year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
  doc.text(pdfSafe([scopeLabel && `Period: ${scopeLabel}`, "Amounts in PHP", `Generated ${generated}`]
    .filter(Boolean).join("   ·   ")), 12, y);
  y = drawHighlights(doc, pageW, y + 5, table.highlights) + 7;

  autoTable(doc, {
    startY: y,
    head: table.head,
    body: table.body,
    foot: table.foot,
    showHead: "everyPage",
    showFoot: "lastPage",
    theme: "plain",
    styles:      { fontSize: 8, cellPadding: { top: 2, bottom: 2, left: 2.5, right: 2.5 }, overflow: "linebreak", textColor: INK },
    headStyles:  { fillColor: MAROON, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5 },
    footStyles:  { fillColor: GOLD_SOFT, textColor: MAROON, fontStyle: "bold", fontSize: 8.5 },
    alternateRowStyles: { fillColor: CREAM },
    columnStyles: [...table.right].reduce((acc, i) => { acc[i] = { halign: "right" }; return acc; }, {}),
    margin: { left: 12, right: 12, top: 16, bottom: 16 },
    didParseCell: (data) => {
      if (data.section !== "body" && table.right.has(data.column.index)) data.cell.styles.halign = "right";
      if (data.section === "body" && table.sub[data.row.index]) {
        data.cell.styles.fontSize = 7;
        data.cell.styles.textColor = MUTED;
        data.cell.styles.fillColor = [255, 255, 255];
        data.cell.styles.cellPadding = { top: 1.2, bottom: 1.2, left: 2.5, right: 2.5 };
      } else if (data.section === "body" && table.sub[data.row.index + 1]) {
        // Grade-level row that has student rows under it.
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = GOLD_SOFT;
        data.cell.styles.textColor = MAROON;
      }
    },
    didDrawCell: (data) => {
      // Thin divider under every body row.
      if (data.section === "body") {
        doc.setDrawColor(236, 229, 215);
        doc.setLineWidth(0.15);
        doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
      }
    },
  });

  // Signature lines (new page if they won't fit).
  let sy = (doc.lastAutoTable?.finalY || y) + 20;
  if (sy + 20 > pageH - 18) { doc.addPage(); sy = 40; }
  const sigW = 70;
  const preparedBy = pdfSafe(currentUserName());
  [["Prepared by", preparedBy], ["Noted by", ""]].forEach(([label, name], i) => {
    const x = i === 0 ? 12 : pageW - 12 - sigW;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...INK);
    if (name) doc.text(name, x + sigW / 2, sy - 2, { align: "center" });
    doc.setDrawColor(...MUTED);
    doc.setLineWidth(0.3);
    doc.line(x, sy, x + sigW, sy);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(label, x + sigW / 2, sy + 4.5, { align: "center" });
  });

  // Running header (pages 2+) and footer on every page, stamped last so
  // "Page X of Y" carries the real total.
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    if (i > 1) drawRunningHeader(doc, pageW, school, title);
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.5);
    doc.line(12, pageH - 12, pageW - 12, pageH - 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(pdfSafe(`${school.name}  ·  Confidential - for internal use`), 12, pageH - 7);
    doc.text(`Page ${i} of ${pages}`, pageW - 12, pageH - 7, { align: "right" });
  }
  doc.setTextColor(...INK);

  doc.save(`${slug(title)}_${todayIso()}.pdf`);
}
